import { Suspense, lazy, useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';

import { useAuth } from './lib/auth';
import { Layout, SkipLink } from './components/Layout';
import { CookieBanner } from './components/CookieBanner';
import { ConfirmProvider } from './components/ConfirmProvider';
import { ErrorBoundary } from './components/ErrorBoundary';

// First-paint pages — kept eager. Login lands first when unauthenticated;
// NotFound is small and used as the 404 inside an already-rendered shell.
import { Login } from './pages/Login';
import { DashboardSetup } from './pages/DashboardSetup';
import { DashboardResetPassword } from './pages/DashboardResetPassword';
import { NotFound } from './pages/NotFound';
// Public marketing landing at `/`. Eager so the front door paints
// instantly rather than flashing a chunk-load spinner.
import { Landing } from './pages/Landing';

import { PortalAuthProvider, usePortalAuth } from './portal/auth';
import { PortalLogin } from './portal/PortalLogin';

// ── Lazy-loaded routes ────────────────────────────────────────────────────
// Each named export is unwrapped into a default for React.lazy. Vite
// produces one chunk per module; modules that export multiple components
// (e.g. FlagQueue) are deduped automatically by the bundler.

const Home = lazy(() =>
  import('./pages/Home').then((m) => ({ default: m.Home })),
);
const CandidateList = lazy(() =>
  import('./pages/CandidateList').then((m) => ({ default: m.CandidateList })),
);
const CandidateNew = lazy(() =>
  import('./pages/CandidateNew').then((m) => ({ default: m.CandidateNew })),
);
const CandidateDetail = lazy(() =>
  import('./pages/CandidateDetail').then((m) => ({ default: m.CandidateDetail })),
);
const ReviewQueue = lazy(() =>
  import('./pages/ReviewQueue').then((m) => ({ default: m.ReviewQueue })),
);
const ReviewDetail = lazy(() =>
  import('./pages/ReviewDetail').then((m) => ({ default: m.ReviewDetail })),
);
const ItemList = lazy(() =>
  import('./pages/ItemList').then((m) => ({ default: m.ItemList })),
);
const ItemUploadQuestionBank = lazy(() =>
  import('./pages/ItemUploadQuestionBank').then((m) => ({
    default: m.ItemUploadQuestionBank,
  })),
);
const ItemDetail = lazy(() =>
  import('./pages/ItemDetail').then((m) => ({ default: m.ItemDetail })),
);
const PortfolioManager = lazy(() =>
  import('./pages/PortfolioManager').then((m) => ({
    default: m.PortfolioManager,
  })),
);
const PortfolioReviewDetail = lazy(() =>
  import('./pages/PortfolioReviewDetail').then((m) => ({
    default: m.PortfolioReviewDetail,
  })),
);
const Stage0List = lazy(() =>
  import('./pages/Stage0List').then((m) => ({ default: m.Stage0List })),
);
const Stage0Detail = lazy(() =>
  import('./pages/Stage0Detail').then((m) => ({ default: m.Stage0Detail })),
);
const FrameworkView = lazy(() =>
  import('./pages/FrameworkView').then((m) => ({ default: m.FrameworkView })),
);
const FrameworkUpload = lazy(() =>
  import('./pages/FrameworkUpload').then((m) => ({ default: m.FrameworkUpload })),
);
const BulkResumeImport = lazy(() =>
  import('./pages/BulkResumeImport').then((m) => ({
    default: m.BulkResumeImport,
  })),
);
const TaxonomyAdmin = lazy(() =>
  import('./pages/TaxonomyAdmin').then((m) => ({ default: m.TaxonomyAdmin })),
);
const ProfileReviewQueue = lazy(() =>
  import('./pages/ProfileReviewQueue').then((m) => ({
    default: m.ProfileReviewQueue,
  })),
);
const PoolAnalytics = lazy(() =>
  import('./pages/PoolAnalytics').then((m) => ({
    default: m.PoolAnalytics,
  })),
);
const UserManagement = lazy(() =>
  import('./pages/UserManagement').then((m) => ({ default: m.UserManagement })),
);
const DashboardAccount = lazy(() =>
  import('./pages/DashboardAccount').then((m) => ({
    default: m.DashboardAccount,
  })),
);
const DashboardSecureAccount = lazy(() =>
  import('./pages/DashboardSecureAccount').then((m) => ({
    default: m.DashboardSecureAccount,
  })),
);
const AuditLog = lazy(() =>
  import('./pages/AuditLog').then((m) => ({ default: m.AuditLog })),
);
const ImplementationStatusQueue = lazy(
  () => import('./pages/admin/ImplementationStatusQueue'),
);
const CandidateJourney = lazy(() =>
  import('./pages/CandidateJourney').then((m) => ({ default: m.CandidateJourney })),
);
// FlagQueueSummary + FlagQueue share a module; both lazy entries resolve
// to the same chunk via Vite's module dedup.
const FlagQueueSummary = lazy(() =>
  import('./pages/FlagQueue').then((m) => ({ default: m.FlagQueueSummary })),
);
const FlagQueue = lazy(() =>
  import('./pages/FlagQueue').then((m) => ({ default: m.FlagQueue })),
);
const ItemHealth = lazy(() =>
  import('./pages/ItemHealth').then((m) => ({ default: m.ItemHealth })),
);
const DataRightsQueue = lazy(() =>
  import('./pages/DataRightsQueue').then((m) => ({
    default: m.DataRightsQueue,
  })),
);
const CandidateScheduler = lazy(() =>
  import('./pages/CandidateScheduler').then((m) => ({
    default: m.CandidateScheduler,
  })),
);
const CandidateExplorer = lazy(() =>
  import('./pages/CandidateExplorer').then((m) => ({
    default: m.CandidateExplorer,
  })),
);
const PortalSetup = lazy(() =>
  import('./portal/PortalSetup').then((m) => ({ default: m.PortalSetup })),
);
const PortalHome = lazy(() =>
  import('./portal/PortalHome').then((m) => ({ default: m.PortalHome })),
);
const PortalAssessment = lazy(() =>
  import('./portal/PortalAssessment').then((m) => ({
    default: m.PortalAssessment,
  })),
);
const PortalAccount = lazy(() =>
  import('./portal/PortalAccount').then((m) => ({ default: m.PortalAccount })),
);
const PortalDataRights = lazy(() =>
  import('./portal/PortalDataRights').then((m) => ({
    default: m.PortalDataRights,
  })),
);
const PortalHelp = lazy(() =>
  import('./portal/PortalHelp').then((m) => ({ default: m.PortalHelp })),
);
// PortalPassport is intentionally not imported. The candidate-facing
// passport surface is disabled in Phase 1 (stakeholder decision: a
// scorecard ships first; passport returns once interview transcription
// lands). The component file is kept for easy re-enable.
// const PortalPassport = lazy(() =>
//   import('./portal/PortalPassport').then((m) => ({
//     default: m.PortalPassport,
//   })),
// );
// PortalPortfolio is the multi-step resume+portfolio wizard. In the
// current phase only Step 1 (resume) is in scope — the component
// auto-restricts itself when window.location is /portal/profile or
// when its `phase1Only` prop is set.
const PortalPortfolioDocument = lazy(() =>
  import('./portal/PortalPortfolioDocument').then((m) => ({
    default: m.PortalPortfolioDocument,
  })),
);
const PortalPortfolio = lazy(() =>
  import('./portal/PortalPortfolio').then((m) => ({
    default: m.PortalPortfolio,
  })),
);
const PortalForgotPassword = lazy(() =>
  import('./portal/PortalForgotPassword').then((m) => ({
    default: m.PortalForgotPassword,
  })),
);
const PortalSecureAccount = lazy(() =>
  import('./portal/PortalSecureAccount').then((m) => ({
    default: m.PortalSecureAccount,
  })),
);
const PortalResetPassword = lazy(() =>
  import('./portal/PortalResetPassword').then((m) => ({
    default: m.PortalResetPassword,
  })),
);

// ── Document title — derives a tab title from the active route so the
// browser tab + screen-reader page-list don't always read "Prism". The
// map is intentionally simple; pages with dynamic identifiers (e.g.
// /candidates/:id) match by prefix and read the friendlier-than-default
// segment label.

const TITLE_MAP: Array<[RegExp | string, string]> = [
  // Staff
  [/^\/dashboard\/?$/, 'Home'],
  [/^\/dashboard\/candidates\/new/, 'Invite candidate'],
  [/^\/dashboard\/candidates\/[^/]+\/journey/, 'Candidate journey'],
  [/^\/dashboard\/candidates\/[^/]+/, 'Candidate detail'],
  [/^\/dashboard\/candidates/, 'Candidates'],
  [/^\/dashboard\/explorer/, 'Candidate explorer'],
  [/^\/dashboard\/reviews\/[^/]+/, 'Score review'],
  [/^\/dashboard\/reviews/, 'Score queue'],
  [/^\/dashboard\/profile-review/, 'Profile review'],
  [/^\/dashboard\/pool-analytics/, 'Pool analytics'],
  [/^\/dashboard\/candidates\/[^/]+\/scheduler/, 'Scheduler'],
  [/^\/dashboard\/flag-queue\/[^/]+/, 'Flag queue'],
  [/^\/dashboard\/flag-queue/, 'Flag queues'],
  [/^\/dashboard\/items\/health/, 'Item health'],
  [/^\/dashboard\/items\/upload/, 'Upload items'],
  [/^\/dashboard\/items\/[^/]+/, 'Item detail'],
  [/^\/dashboard\/items/, 'Item bank'],
  [/^\/dashboard\/portfolio-reviews/, 'Portfolio review'],
  [/^\/dashboard\/portfolio/, 'Portfolio'],
  [/^\/dashboard\/framework\/upload/, 'Upload framework'],
  [/^\/dashboard\/framework/, 'Competency framework'],
  [/^\/dashboard\/data-rights/, 'Data-rights queue'],
  [/^\/dashboard\/users/, 'Users'],
  [/^\/dashboard\/audit/, 'Audit log'],
  [/^\/dashboard\/login/, 'Sign in'],
  // Portal
  [/^\/portal\/?$/, 'Home'],
  [/^\/portal\/profile/, 'Profile'],
  [/^\/portal\/portfolio/, 'Profile'],
  [/^\/portal\/assessment/, 'Assessment'],
  [/^\/portal\/account/, 'Account'],
  [/^\/portal\/data-rights/, 'Data rights'],
  [/^\/portal\/help/, 'Help'],
  [/^\/portal\/login/, 'Sign in'],
  [/^\/portal\/setup/, 'Set up account'],
  [/^\/portal\/forgot-password/, 'Reset password'],
  [/^\/portal\/reset-password/, 'Reset password'],
  [/^\/portal\/secure-account/, 'Secure your account'],
  [/^\/dashboard\/secure-account/, 'Secure your account'],
];

function titleForPath(path: string): string {
  for (const [matcher, label] of TITLE_MAP) {
    if (typeof matcher === 'string'
      ? path === matcher
      : matcher.test(path)) {
      return `${label} · Prism`;
    }
  }
  return 'Prism';
}

function RouteTitle() {
  const location = useLocation();
  useEffect(() => {
    document.title = titleForPath(location.pathname);
  }, [location.pathname]);
  return null;
}

// ── Spinner shared between guards ──────────────────────────────────────────

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)]">
      <span
        aria-label="Loading"
        role="status"
        className="h-8 w-8 animate-spin rounded-full border-[2.5px] border-[var(--color-primary)] border-t-transparent"
      />
    </div>
  );
}

// ── Assessor guards (staff session) ────────────────────────────────────────

function RequireStaffAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullScreenSpinner />;
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/dashboard/login"
        state={{ from: location }}
        replace
      />
    );
  }
  // ErrorBoundary inside Layout so the chrome (sidebar/header) stays
  // intact even when an individual page component crashes.
  // Suspense inside ErrorBoundary so chunk-load failures fall through to
  // the boundary's friendly fallback instead of blanking the SPA.
  return (
    <ConfirmProvider>
      <RouteTitle />
      <SkipLink />
      <Layout>
        <ErrorBoundary key={location.pathname}>
          <Suspense fallback={<FullScreenSpinner />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </Layout>
    </ConfirmProvider>
  );
}

function RedirectIfStaffAuthed() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <FullScreenSpinner />;
  if (isAuthenticated) return <Navigate to="/dashboard/" replace />;
  return <Outlet />;
}

// ── Candidate-portal guards (separate cookie / context) ────────────────────

function PortalShell() {
  return (
    <PortalAuthProvider>
      <ConfirmProvider>
        <RouteTitle />
        <Outlet />
      </ConfirmProvider>
    </PortalAuthProvider>
  );
}

function RequirePortalAuth() {
  const { isAuthenticated, isLoading } = usePortalAuth();
  const location = useLocation();
  if (isLoading) return <FullScreenSpinner />;
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/portal/login"
        state={{ from: location }}
        replace
      />
    );
  }
  return (
    <ErrorBoundary key={location.pathname}>
      <Suspense fallback={<FullScreenSpinner />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
}

function RedirectIfPortalAuthed() {
  const { isAuthenticated, isLoading } = usePortalAuth();
  if (isLoading) return <FullScreenSpinner />;
  if (isAuthenticated) return <Navigate to="/portal/" replace />;
  // Suspense here covers PortalSetup which is also lazy.
  return (
    <Suspense fallback={<FullScreenSpinner />}>
      <Outlet />
    </Suspense>
  );
}

// Role-aware dashboard landing. item_bank_manager has no candidate-dashboard
// access — /api/dashboard/home and /workbench require CANDIDATE_VIEW_ASSIGNED,
// which that role lacks — so it lands on its own surface (Browse Items) instead
// of the candidate Home, which would 403.
function DashboardIndex() {
  const { user } = useAuth();
  if (user?.role === 'item_bank_manager') {
    return <Navigate to="/dashboard/items" replace />;
  }
  return <Home />;
}

// ── Router ─────────────────────────────────────────────────────────────────

const router = createBrowserRouter([
  // -------- Public marketing landing --------
  // Served at the root. Data-free; routes candidates to /portal/login and
  // staff to /dashboard/login. Everything else still falls through to the
  // dashboard via the catch-all at the bottom.
  { path: '/', element: <Landing /> },

  // -------- Assessor dashboard --------
  {
    path: '/dashboard',
    children: [
      {
        element: <RedirectIfStaffAuthed />,
        children: [
          { path: 'login', element: <Login /> },
          // Public route — the user lands here from the invite email
          // and has no session yet. Token from query string is the
          // gate; backend validates it.
          { path: 'setup', element: <DashboardSetup /> },
          // Public password-reset landing (no session yet). The "forgot"
          // request step lives inline on the login page; this page consumes
          // the token from the reset email and sets the new password.
          { path: 'reset-password', element: <DashboardResetPassword /> },
        ],
      },
      {
        // Public "was this you?" page reached from the password-changed
        // email. Rendered regardless of session (a victim whose password
        // was changed is already signed out), so it lives outside both the
        // redirect-if-authed and require-auth guards.
        element: (
          <Suspense fallback={<FullScreenSpinner />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          { path: 'secure-account', element: <DashboardSecureAccount /> },
        ],
      },
      {
        element: <RequireStaffAuth />,
        children: [
          { index: true, element: <DashboardIndex /> },
          { path: 'candidates', element: <CandidateList /> },
          { path: 'explorer', element: <CandidateExplorer /> },
          { path: 'candidates/new', element: <CandidateNew /> },
          { path: 'candidates/:id', element: <CandidateDetail /> },
          { path: 'candidates/:id/journey', element: <CandidateJourney /> },
          { path: 'flag-queue', element: <FlagQueueSummary /> },
          { path: 'flag-queue/:queue', element: <FlagQueue /> },
          { path: 'items/health', element: <ItemHealth /> },
          { path: 'data-rights', element: <DataRightsQueue /> },
          { path: 'reviews', element: <ReviewQueue /> },
          { path: 'profile-review', element: <ProfileReviewQueue /> },
          { path: 'pool-analytics', element: <PoolAnalytics /> },
          { path: 'candidates/:id/scheduler', element: <CandidateScheduler /> },
          { path: 'candidates/bulk-import', element: <BulkResumeImport /> },
          { path: 'reviews/:scoreId', element: <ReviewDetail /> },
          { path: 'items', element: <ItemList /> },
          {
            // Legacy upload page expected a validate/confirm protocol the
            // backend never implemented (it crashed on a successful import).
            // Redirect to the working question-bank uploader.
            path: 'items/upload',
            element: <Navigate to="/dashboard/items/upload-question-bank" replace />,
          },
          { path: 'items/upload-question-bank', element: <ItemUploadQuestionBank /> },
          { path: 'items/:id', element: <ItemDetail /> },
          { path: 'portfolio', element: <PortfolioManager /> },
          {
            path: 'portfolio-reviews/:scoreId',
            element: <PortfolioReviewDetail />,
          },
          { path: 'stage0', element: <Stage0List /> },
          { path: 'stage0/:id', element: <Stage0Detail /> },
          { path: 'framework', element: <FrameworkView /> },
          { path: 'framework/upload', element: <FrameworkUpload /> },
          { path: 'framework/taxonomy', element: <TaxonomyAdmin /> },
          { path: 'users', element: <UserManagement /> },
          { path: 'account', element: <DashboardAccount /> },
          { path: 'audit', element: <AuditLog /> },
          { path: 'content-review', element: <ImplementationStatusQueue /> },
        ],
      },
      { path: '', element: <Navigate to="/dashboard/" replace /> },
      // Real 404 inside the staff shell so unknown URLs feel like a page,
      // not a silent redirect.
      {
        element: <RequireStaffAuth />,
        children: [
          { path: '*', element: <NotFound homeTo="/dashboard/" homeLabel="Back to the dashboard" /> },
        ],
      },
    ],
  },

  // -------- Candidate portal --------
  {
    path: '/portal',
    element: <PortalShell />,
    children: [
      {
        element: <RedirectIfPortalAuthed />,
        children: [
          { path: 'login', element: <PortalLogin /> },
          // Self-service password reset — both pages are unauthed.
          { path: 'forgot-password', element: <PortalForgotPassword /> },
          { path: 'reset-password', element: <PortalResetPassword /> },
        ],
      },
      // /portal/setup is INTENTIONALLY outside RedirectIfPortalAuthed.
      // The setup token in the URL identifies the *new* candidate; if
      // someone else's prism_session cookie is present in this browser
      // the redirect-if-authed wrapper would bounce the visit to that
      // OTHER candidate's portal home — and the new candidate's setup
      // would never start. The setup flow is token-gated, not session-
      // gated; safe to render regardless of any active session.
      {
        element: (
          <Suspense fallback={<FullScreenSpinner />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          { path: 'setup', element: <PortalSetup /> },
          // Public "was this you?" page reached from the password-changed
          // email; renders regardless of session (a victim is signed out).
          { path: 'secure-account', element: <PortalSecureAccount /> },
        ],
      },
      {
        element: <RequirePortalAuth />,
        children: [
          { index: true, element: <PortalHome /> },
          { path: 'assessment', element: <PortalAssessment /> },
          // Profile (resume submission) is Step 1 of the old multi-step
          // PortalPortfolio wizard. Steps 2-4 are Stage 4 and disabled.
          { path: 'profile', element: <PortalPortfolio /> },
          // Legacy alias so existing invite emails / bookmarks still land.
          { path: 'portfolio', element: <PortalPortfolio /> },
          // Post-assessment portfolio "document" (storyboarding & QA).
          { path: 'portfolio-document', element: <PortalPortfolioDocument /> },
          { path: 'account', element: <PortalAccount /> },
          { path: 'data-rights', element: <PortalDataRights /> },
          { path: 'help', element: <PortalHelp /> },
        ],
      },
      { path: '', element: <Navigate to="/portal/" replace /> },
      {
        path: '*',
        element: <NotFound homeTo="/portal/" homeLabel="Back to the portal" />,
      },
    ],
  },

  // -------- Anything else --------
  // Default to the assessor dashboard. The candidate portal entry point
  // is /portal/ which the candidate's invite email will link to directly.
  { path: '*', element: <Navigate to="/dashboard/" replace /> },
]);

export default function App() {
  return (
    <>
      <RouterProvider router={router} />
      <CookieBanner />
    </>
  );
}
